import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

/**
 * Detect if we should skip parallax (mobile / reduced-motion).
 * Mobile devices repaint expensively when many scroll-listeners fire — so
 * on touch devices we render children statically.
 */
function useIsMobileOrReduced() {
  const reduced = useReducedMotion();
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(pointer: coarse), (max-width: 1023px)");
    const on = () => setMobile(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return mobile || !!reduced;
}

/**
 * Parallax — content moves at a different speed to the scroll.
 * `speed` -1 to 1. Negative = moves down slower. Positive = moves up faster.
 * On mobile/reduced-motion, renders children with no transforms (no jank).
 */
export default function Parallax({ children, speed = 0.25, className = "" }) {
  const ref = useRef(null);
  const disabled = useIsMobileOrReduced();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [`${speed * -80}px`, `${speed * 80}px`]);

  if (disabled) {
    return <div ref={ref} className={`relative ${className}`}>{children}</div>;
  }
  return (
    <div ref={ref} className={`relative ${className}`}>
      <motion.div style={{ y, willChange: "transform" }}>{children}</motion.div>
    </div>
  );
}

/**
 * ParallaxImage — image-only variant with subtle scale + Y shift.
 * On mobile, renders a static image (no transforms) to prevent flashing.
 */
export function ParallaxImage({ src, alt = "", className = "" }) {
  const ref = useRef(null);
  const disabled = useIsMobileOrReduced();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1.0, 1.08]);

  if (disabled) {
    return (
      <div ref={ref} className={`absolute inset-0 overflow-hidden ${className}`}>
        <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div ref={ref} className={`absolute inset-0 overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y, scale, willChange: "transform" }}
        className="absolute inset-0 w-full h-full object-cover"
      />
    </div>
  );
}
